import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Card,
  Group,
  Text,
  Badge,
  Stack,
  Grid,
  Timeline,
  Button,
  Modal,
  Textarea,
  Select,
  LoadingOverlay,
  List,
  ThemeIcon,
  Title,
  TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import {
  IconCircleCheck,
  IconCircleDashed,
} from '@tabler/icons-react';
import { useOrdersStore, updateOrderStatus, completeTask } from '../store/orders';
import { OrderStatus, StatusTask } from '../types';

export function OrderDetails() {
  const { orderId } = useParams();
  const { selectedOrder, setSelectedOrder, isLoading } = useOrdersStore();
  const [statuses, setStatuses] = useState<OrderStatus[]>([]);
  const [tasks, setTasks] = useState<StatusTask[]>([]);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<StatusTask | null>(null);

  const statusForm = useForm({
    initialValues: {
      status_id: '',
      notes: '',
    },
  });

  const taskForm = useForm({
    initialValues: {
      completed_by: '',
      notes: '',
    },
  });

  useEffect(() => {
    if (orderId) {
      // Fetch order details
      fetch(`/api/orders/${orderId}`)
        .then((res) => res.json())
        .then(setSelectedOrder);

      // Fetch available statuses
      fetch('/api/statuses')
        .then((res) => res.json())
        .then(setStatuses);
    }

    return () => setSelectedOrder(null);
  }, [orderId]);

  useEffect(() => {
    if (selectedOrder) {
      // Fetch tasks for current status
      fetch(`/api/tasks?status_id=${selectedOrder.current_status.id}`)
        .then((res) => res.json())
        .then(setTasks);
    }
  }, [selectedOrder?.current_status.id]);

  if (!selectedOrder) return null;

  const handleStatusUpdate = async (values: typeof statusForm.values) => {
    await updateOrderStatus(
      parseInt(orderId!),
      parseInt(values.status_id),
      values.notes
    );
    setStatusModalOpen(false);
    statusForm.reset();
  };

  const handleTaskComplete = async (values: typeof taskForm.values) => {
    if (!selectedTask) return;

    await completeTask(
      parseInt(orderId!),
      selectedTask.id,
      values.completed_by,
      values.notes
    );
    setTaskModalOpen(false);
    setSelectedTask(null);
    taskForm.reset();
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'new':
        return 'blue';
      case 'in progress':
        return 'yellow';
      case 'completed':
        return 'green';
      case 'on hold':
        return 'orange';
      case 'cancelled':
        return 'red';
      default:
        return 'gray';
    }
  };

  return (
    <div style={{ position: 'relative' }}>
      <LoadingOverlay visible={isLoading} />
      <Stack>
        <Card>
          <Grid>
            <Grid.Col span={8}>
              <Group>
                <Text size="xl" style={{ fontWeight: 700 }}>
                  {selectedOrder.reference_number}
                </Text>
                <Badge
                  size="lg"
                  color={getStatusColor(selectedOrder.current_status.name)}
                  variant="light"
                >
                  {selectedOrder.current_status.name}
                </Badge>
              </Group>
              <Text size="sm" color="dimmed" mt={4}>
                Created on{' '}
                {new Date(selectedOrder.created_at).toLocaleDateString()}
              </Text>
            </Grid.Col>
            <Grid.Col span={4}>
              <Group style={{ justifyContent: 'flex-end' }}>
                <Button
                  variant="light"
                  onClick={() => setStatusModalOpen(true)}
                >
                  Update Status
                </Button>
              </Group>
            </Grid.Col>
          </Grid>
        </Card>

        <Grid>
          <Grid.Col span={4}>
            <Stack>
              <Card>
                <Title order={4}>Client Details</Title>
                <Text mt="md">{selectedOrder.client.name}</Text>
                <Text size="sm" color="dimmed">
                  {selectedOrder.client.email}
                </Text>
                {selectedOrder.client.phone && (
                  <Text size="sm" color="dimmed">
                    {selectedOrder.client.phone}
                  </Text>
                )}
              </Card>

              <Card>
                <Title order={4}>Installation Address</Title>
                <Text mt="md">{selectedOrder.address.street_address}</Text>
                <Text size="sm">
                  {selectedOrder.address.suburb}, {selectedOrder.address.city}
                </Text>
                <Text size="sm">{selectedOrder.address.postal_code}</Text>
              </Card>

              <Card>
                <Title order={4}>Package Details</Title>
                <Text mt="md" style={{ fontWeight: 500 }}>
                  {selectedOrder.package_name}
                </Text>
                <Text size="sm">{selectedOrder.package_speed}</Text>
                <Text size="sm" color="dimmed">
                  R {selectedOrder.package_price.toFixed(2)}
                </Text>
                <Text size="sm" color="dimmed" mt="xs">
                  FNO: {selectedOrder.fiber_network_operator.name}
                </Text>
              </Card>
            </Stack>
          </Grid.Col>

          <Grid.Col span={8}>
            <Stack>
              <Card>
                <Title order={4} mb="md">
                  Required Tasks
                </Title>
                <List
                  spacing="xs"
                  size="sm"
                  center
                  icon={
                    <ThemeIcon color="blue" size={24} radius="xl">
                      <IconCircleDashed size={16} />
                    </ThemeIcon>
                  }
                >
                  {tasks.map((task) => {
                    const isCompleted = selectedOrder.completed_tasks.some(
                      (ct) => ct.status_task_id === task.id
                    );
                    return (
                      <List.Item
                        key={task.id}
                        icon={
                          <ThemeIcon
                            color={isCompleted ? 'green' : 'blue'}
                            size={24}
                            radius="xl"
                          >
                            {isCompleted ? (
                              <IconCircleCheck size={16} />
                            ) : (
                              <IconCircleDashed size={16} />
                            )}
                          </ThemeIcon>
                        }
                      >
                        <Group style={{ justifyContent: 'space-between' }}>
                          <div>
                            <Text>{task.name}</Text>
                            {task.description && (
                              <Text size="xs" color="dimmed">
                                {task.description}
                              </Text>
                            )}
                          </div>
                          {!isCompleted && (
                            <Button
                              variant="light"
                              size="xs"
                              onClick={() => {
                                setSelectedTask(task);
                                setTaskModalOpen(true);
                              }}
                            >
                              Complete
                            </Button>
                          )}
                        </Group>
                      </List.Item>
                    );
                  })}
                </List>
              </Card>

              <Card>
                <Title order={4} mb="md">
                  Status History
                </Title>
                <Timeline active={selectedOrder.status_history.length - 1}>
                  {selectedOrder.status_history.map((history) => (
                    <Timeline.Item
                      key={history.id}
                      title={
                        statuses.find((s) => s.id === history.status_id)?.name
                      }
                    >
                      <Text size="xs" mt={4}>
                        {new Date(history.created_at).toLocaleString()}
                      </Text>
                      {history.notes && (
                        <Text size="sm" color="dimmed" mt={4}>
                          {history.notes}
                        </Text>
                      )}
                    </Timeline.Item>
                  ))}
                </Timeline>
              </Card>
            </Stack>
          </Grid.Col>
        </Grid>
      </Stack>

      <Modal
        opened={statusModalOpen}
        onClose={() => setStatusModalOpen(false)}
        title="Update Order Status"
      >
        <form onSubmit={statusForm.onSubmit(handleStatusUpdate)}>
          <Stack>
            <Select
              label="New Status"
              placeholder="Select status"
              data={statuses.map((status) => ({
                value: status.id.toString(),
                label: status.name,
              }))}
              required
              {...statusForm.getInputProps('status_id')}
            />
            <Textarea
              label="Notes"
              placeholder="Add notes about this status change"
              {...statusForm.getInputProps('notes')}
            />
            <Button type="submit">Update Status</Button>
          </Stack>
        </form>
      </Modal>

      <Modal
        opened={taskModalOpen}
        onClose={() => {
          setTaskModalOpen(false);
          setSelectedTask(null);
        }}
        title="Complete Task"
      >
        <form onSubmit={taskForm.onSubmit(handleTaskComplete)}>
          <Stack>
            <TextInput
              label="Completed By"
              placeholder="Enter your name"
              required
              {...taskForm.getInputProps('completed_by')}
            />
            <Textarea
              label="Notes"
              placeholder="Add any notes about this task completion"
              {...taskForm.getInputProps('notes')}
            />
            <Button type="submit">Mark as Complete</Button>
          </Stack>
        </form>
      </Modal>
    </div>
  );
}
